/**
 * Small DOM/reactivity helpers extracted from the Shoptet toolbox
 * (shoptet-scripts/toolbox-v2.js), adapted to the npm `vanjs-core` build.
 *
 * Exposes the same `h()`, `val()`, and `classNames()` behaviour used across the
 * storefront scripts so the client code reads the same way.
 */
import van from "vanjs-core";

const stateProto = Object.getPrototypeOf(van.state());

/**
 * Unwrap a Van value: returns the current value of a state, calls a derivation
 * function, or returns a plain value unchanged.
 */
export const val = (v) => {
  const proto = Object.getPrototypeOf(v ?? 0);
  if(proto === stateProto) return v.val;
  if(proto === Function.prototype) return v();
  return v;
};

/** If `value` is a function, call it with `args`; otherwise return it as-is. */
export const resolveValue = (value, ...args) =>
  typeof value === "function" ? value(...args) : value;

export const isPlainObject = (obj) =>
  obj != null && [null, Object.prototype].includes(Object.getPrototypeOf(obj));

export const isString = (obj) =>
  Object.prototype.toString.call(obj) === "[object String]";

/** Join class name strings/arrays into one space-separated string, dropping falsy values. */
export const classNames = (...names) =>
  names.flat(Infinity).filter(Boolean).join(" ");

/** Like {@link classNames} but returns an array of individual tokens. */
export const classNamesArray = (...names) => {
  const str = classNames(names).trim();
  return str !== "" ? str.split(/\s+/) : [];
};

let idCounter = 0;
/** Monotonically incrementing unique id, optionally prefixed. */
export const uniqueId = (prefix) => {
  const id = String(++idCounter);
  return prefix ? prefix + id : id;
};

/**
 * Hyperscript helper with CSS-selector shorthand:
 *   h("div.foo.bar")     -> <div class="foo bar">
 *   h("span#id", props)  -> <span id="id" ...>
 *   h(".foo", child)     -> <div class="foo"> (tag defaults to div)
 *
 * A reactive `class` prop is preserved and merged with the shorthand classes.
 */
export function h(tagName, props, ...children) {
  if(!isPlainObject(props)) {
    if(props !== undefined) children.unshift(props);
    props = {};
  }

  const parts = tagName.split(/([.#]?[a-zA-Z0-9_:-]+)/).filter(Boolean);
  tagName = (!parts.length || parts[0].charAt(0) === "." || parts[0].charAt(0) === "#") ? "div" : parts.shift();
  const classList = [];
  for(const part of parts) {
    if(part.charAt(0) === ".")
      classList.push(part.slice(1));
    else if(part.charAt(0) === "#")
      props.id = part.slice(1);
  }
  if(classList.length) {
    if(props.class) {
      const propsClass = props.class;
      props.class = () => classNames(classList, val(propsClass));
    } else {
      props.class = classList.join(" ");
    }
  }

  return van.tags[tagName](props, ...children);
}

export { van };
